import { NavLink } from 'react-router-dom'
import {
  LayoutDashboard,
  CalendarDays,
  Users,
  Camera,
  Palette,
  Banknote,
  Building2,
  Shapes,
  ChevronLeft,
  Files,
  Package,
  UserCog,
  Sparkles,
  Music2,
  ShoppingBag,
  Images,
  BarChart2,
  HelpCircle,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { useUIStore } from '@/stores/ui-store'
import { useAuthStore } from '@/stores/auth-store'

const navGroups = [
  {
    items: [
      { to: '/', label: 'Dashboard', icon: LayoutDashboard, end: true },
    ],
  },
  {
    label: 'Konten',
    items: [
      { to: '/settings/profile', label: 'Profil Grup', icon: Building2 },
      { to: '/members', label: 'Member', icon: Users },
      { to: '/events', label: 'Event', icon: CalendarDays },
      { to: '/music', label: 'Musik', icon: Music2 },
      { to: '/shop', label: 'Shop', icon: ShoppingBag },
      { to: '/faq', label: 'FAQ', icon: HelpCircle },
      { to: '/media', label: 'Media Library', icon: Images },
    ],
  },
  {
    label: 'Cheki',
    items: [
      { to: '/cheki', label: 'Form Cheki', icon: Camera },
      { to: '/cheki/analytics', label: 'Analitik', icon: BarChart2 },
      // { to: '/cheki/templates', label: 'Template', icon: FileText },
      { to: '/cheki/setting', label: 'Pengaturan Cheki', icon: Banknote },
    ],
  },
  {
    label: 'Halaman Publik',
    items: [
      { to: '/settings/thema', label: 'Tema Preset', icon: Sparkles },
      { to: '/settings/theme', label: 'Tema Visual', icon: Palette },
      { to: '/pages', label: 'Kelola Halaman', icon: Files },
      { to: '/gallery', label: 'Galeri Komponen', icon: Shapes },
    ],
  },
  {
    label: 'Pengaturan',
    items: [
      { to: '/settings/account', label: 'Akun', icon: UserCog },
      { to: '/settings/subscription', label: 'Langganan', icon: Package },
    ],
  },
]

export function Sidebar() {
  const { sidebarOpen, toggleSidebar } = useUIStore()
  const tenant = useAuthStore((s) => s.currentTenant)

  return (
    <aside
      className={cn(
        'fixed inset-y-0 left-0 z-50 flex flex-col bg-sidebar border-r border-sidebar-border transition-all duration-300',
        sidebarOpen ? 'w-64' : 'w-16'
      )}
    >
      {/* Logo / Tenant name */}
      <div className="flex h-16 items-center gap-3 px-4 border-b border-sidebar-border">
        {/* Avatar — always visible */}
        <div className="h-8 w-8 shrink-0 rounded-full bg-sidebar-accent flex items-center justify-center overflow-hidden">
          {tenant?.logo_url ? (
            <img src={tenant.logo_url} alt="" className="h-full w-full object-cover" />
          ) : (
            <span className="text-xs font-bold text-sidebar-accent-foreground">
              {tenant?.name?.[0]?.toUpperCase() ?? 'O'}
            </span>
          )}
        </div>

        {/* Name + slug — only when expanded */}
        {sidebarOpen && (
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-semibold text-sidebar-foreground">
              {tenant?.name ?? 'OneIdol'}
            </p>
            {tenant?.slug && (
              <p className="truncate text-xs text-sidebar-foreground/50">
                @{tenant.slug}
              </p>
            )}
          </div>
        )}

        <button
          onClick={toggleSidebar}
          className="rounded-md p-1.5 text-sidebar-foreground hover:bg-sidebar-accent ml-auto shrink-0"
          aria-label="Toggle sidebar"
        >
          <ChevronLeft
            className={cn('h-4 w-4 transition-transform', !sidebarOpen && 'rotate-180')}
          />
        </button>
      </div>

      {/* Nav items */}
      <nav className="flex-1 overflow-y-auto py-4 px-2 space-y-4">
        {navGroups.map((group, gi) => (
          <div key={gi}>
            {group.label && sidebarOpen && (
              <p className="mb-1 px-3 text-xs font-semibold uppercase tracking-wider text-sidebar-foreground/40">
                {group.label}
              </p>
            )}
            {group.label && !sidebarOpen && gi > 0 && (
              <div className="mx-3 mb-1 border-t border-sidebar-border" />
            )}
            <div className="space-y-0.5">
              {group.items.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={'end' in item ? item.end : undefined}
                  className={({ isActive }) =>
                    cn(
                      'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                      isActive
                        ? 'bg-sidebar-accent text-sidebar-accent-foreground'
                        : 'text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
                      !sidebarOpen && 'justify-center'
                    )
                  }
                >
                  <item.icon className="h-4 w-4 shrink-0" />
                  {sidebarOpen && <span>{item.label}</span>}
                </NavLink>
              ))}
            </div>
          </div>
        ))}
      </nav>
    </aside>
  )
}
